import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiImage, FiLoader, FiUploadCloud, FiDownload, FiInfo, FiZap, FiShield, FiClock, FiCheck, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useImageActions } from "../../hooks/useImageActions";
import { useNotification } from "../../context/NotificationContext";

interface Image {
  id: number;
  filename: string;
  url: string;
  originalSize: number;
  compressedSize: number;
}

export const Uploader = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { fetchCompressedImages, uploadImage, downloadImage } = useImageActions();
  const { showNotification } = useNotification();
  const [upload, setUpload] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imagefetch, setImagefetch] = useState<Image[]>([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/signin");
    }
  }, [isAuthenticated, navigate]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} bytes`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  useEffect(() => {
    const getImages = async () => {
      try {
        const data = await fetchCompressedImages();
        setImagefetch(data);
      } catch (err: any) {
        console.error("Fetch error:", err);
      }
    };
    if (isAuthenticated) getImages();
  }, [isAuthenticated, fetchCompressedImages]);

  const handleDownload = async (id: number, filename: string) => {
    try {
      const blob = await downloadImage(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification(`Downloaded ${filename}`, "success");
    } catch (err: any) {
      showNotification(`Failed to download ${filename}: ${err.message}`, "error");
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!upload) {
      showNotification("Please select an image to upload.", "error");
      return;
    }

    const fileData = new FormData();
    fileData.append("file", upload);

    try {
      setLoading(true);
      await uploadImage(fileData, (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percent);
        }
      });
      showNotification("Image optimized successfully!", "success");
      setUpload(null);
      setProgress(0);
      const refreshedData = await fetchCompressedImages();
      setImagefetch(refreshedData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || `Upload failed: ${err.message}`;
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 hero-bg py-5 mt-5">
      <div className="blob animate-morph bg-primary" style={{ top: '10%', left: '-10%', width: '600px', height: '600px', opacity: 0.1 }}></div>
      <div className="blob animate-morph bg-secondary" style={{ bottom: '10%', right: '-10%', width: '500px', height: '500px', opacity: 0.1, animationDelay: '-3s' }}></div>

      <div className="container py-5 animate-fade-in-up z-10 position-relative">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card-premium overflow-hidden border-0 shadow-2xl glass">
              <div className="card-body p-0">
                 <div className="row g-0">
                  <div className="col-lg-5 bg-dark p-4 p-md-5 d-flex flex-column justify-content-between text-white">
                    <div>
                       <div className="bg-primary d-inline-flex p-3 rounded-4 mb-4 shadow-glow">
                          <FiImage size={32} />
                       </div>
                       <h2 className="display-6 fw-900 mb-3" style={{ letterSpacing: '-1px' }}>Asset Optimizer</h2>
                       <p className="opacity-50 fw-medium">Professional-grade image compression with neural-network optimization.</p>
                       
                       <div className="mt-4 mt-lg-5 d-flex flex-column gap-3 mb-4 mb-lg-0">
                          <div className="d-flex align-items-center gap-3 glass p-3 rounded-4 border-white border-opacity-10">
                             <FiZap className="text-warning" />
                             <span className="small fw-bold">Neural Compression</span>
                          </div>
                          <div className="d-flex align-items-center gap-3 glass p-3 rounded-4 border-white border-opacity-10">
                             <FiShield className="text-success" />
                             <span className="small fw-bold">Lossless Quality</span>
                          </div>
                       </div>
                    </div>
                    
                    <div className="mt-4 mt-lg-5">
                       <button 
                         className="btn glass w-100 rounded-pill py-3 fw-900 text-white border-0 shadow-lg d-flex align-items-center justify-content-center gap-2"
                         onClick={() => setShowModal(true)}
                       >
                         <FiClock /> View History
                       </button>
                    </div>
                  </div>

                  <div className="col-lg-7 p-4 p-md-5 bg-white">

                    <form onSubmit={handleUpload}>
                      <div
                        className={`border-3 border-dashed rounded-5 p-4 p-md-5 text-center mb-4 transition-all cursor-pointer position-relative overflow-hidden hover-scale group ${upload ? 'border-primary bg-primary bg-opacity-5' : 'border-secondary-subtle bg-light'}`}
                        onClick={() => document.getElementById("img-upload")?.click()}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary') }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary') }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files?.[0];
                          if (file?.type.startsWith('image/')) {
                            setUpload(file);
                          }
                        }}
                        style={{ minHeight: '280px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <div className="position-absolute inset-0 bg-primary opacity-0 group-hover:opacity-5 transition-opacity"></div>
                        <div className={`mb-4 transition-all duration-500 ${upload ? 'scale-110 animate-float' : ''}`}>
                          <FiUploadCloud size={80} className={upload ? 'text-primary' : 'text-muted opacity-30'} />
                        </div>
                        <h4 className="fw-900 text-dark mb-2">{upload ? upload.name : "Select Asset"}</h4>
                        <p className="text-muted small fw-bold">{upload ? formatFileSize(upload.size) : "PNG, JPG, WebP up to 20MB"}</p>
                        
                        <input
                          id="img-upload"
                          type="file"
                          accept="image/*"
                          className="d-none"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              setUpload(e.target.files[0]);
                            }
                          }}
                        />
                      </div>

                      {upload && (
                        <div className="glass p-3 rounded-4 mb-4 border-0 shadow-sm animate-fade-in-up d-flex align-items-center gap-3">
                           <div className="position-relative">
                              <img
                                src={URL.createObjectURL(upload)}
                                alt="Preview"
                                className="rounded-3 shadow-md"
                                style={{ width: "64px", height: "64px", objectFit: "cover" }}
                              />
                           </div>
                           <div className="overflow-hidden flex-grow-1">
                              <p className="mb-0 fw-900 text-truncate text-dark">{upload.name}</p>
                              <p className="mb-0 fw-bold text-muted x-small">ORIGINAL: {formatFileSize(upload.size)}</p>
                           </div>
                           <div className="bg-primary-subtle text-primary p-2 rounded-3">
                              <FiCheck />
                           </div>
                        </div>
                      )}

                      {loading && (
                        <div className="mb-4 animate-fade-in-up">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="fw-900 small text-primary uppercase tracking-wider">Neural crunching...</span>
                            <span className="fw-900 small">{progress}%</span>
                          </div>
                          <div className="progress bg-light rounded-pill overflow-hidden" style={{ height: "10px" }}>
                            <div 
                               className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                               style={{ width: `${progress}%`, boxShadow: '0 0 20px var(--primary-glow)' }}
                            ></div>
                          </div>
                        </div>
                      )}

                      <button
                        type="submit"
                        className="btn-premium w-100 py-4 fs-4 d-flex align-items-center justify-content-center gap-3 shadow-2xl"
                        disabled={loading || !upload}
                      >
                        {loading ? <FiLoader className="animate-spin" /> : <FiZap />}
                        {loading ? "OPTIMIZING..." : "OPTIMIZE NOW"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced History Modal */}
      <div className={`modal fade ${showModal ? "show" : ""}`} style={{ display: showModal ? "block" : "none", zIndex: 1100 }} tabIndex={-1}>
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content rounded-5 border-0 glass-dark shadow-2xl overflow-hidden" style={{ maxHeight: '90vh' }}>
            <div className="modal-header border-bottom border-white border-opacity-10 p-4">
              <div className="d-flex align-items-center gap-3">
                 <div className="bg-primary p-3 rounded-4 text-white shadow-glow"><FiImage size={24} /></div>
                 <div>
                    <h4 className="fw-900 text-white mb-0">Optimization Gallery</h4>
                    <p className="text-light opacity-50 small mb-0 fw-bold">Management and retrieval of optimized assets.</p>
                 </div>
              </div>
              <button 
                 type="button" 
                 className="btn-close-custom" 
                 onClick={() => setShowModal(false)}
                 aria-label="Close"
               >
                 <FiX size={24} />
               </button>
            </div>
            <div className="modal-body p-4 p-md-5 custom-scrollbar bg-dark bg-opacity-20">
              {imagefetch.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-4 opacity-10"><FiImage size={100} className="text-white" /></div>
                  <p className="text-white opacity-40 fw-900 fs-4">No assets optimized yet.</p>
                </div>
              ) : (
                <div className="row g-4">
                  {imagefetch.map((image) => (
                    <div key={image.id} className="col-md-6 col-xl-4">
                      <div className="glass rounded-5 overflow-hidden h-100 transition-all hover-scale border-white border-opacity-10 group shadow-xl">
                        <div className="position-relative overflow-hidden" style={{ height: "200px" }}>
                           <img
                             src={image.url}
                             alt={image.filename}
                             className="w-100 h-100 transition-all duration-700"
                             style={{ objectFit: "cover", filter: 'brightness(0.9)' }}
                           />
                           <div className="position-absolute top-0 end-0 m-3">
                              <div className="badge glass-dark rounded-pill px-3 py-2 fw-900 border-white border-opacity-20 text-success">
                                 -{Math.round((1 - image.compressedSize / image.originalSize) * 100)}%
                              </div>
                           </div>
                        </div>
                        <div className="card-body p-4">
                          <h6 className="fw-900 text-truncate mb-3 text-white" title={image.filename}>
                            {image.filename}
                          </h6>
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                              <span className="text-light opacity-30 small d-block fw-bold uppercase">Optimized</span>
                              <span className="text-success fw-900 fs-5">{formatFileSize(image.compressedSize)}</span>
                            </div>
                            <div className="text-end">
                              <span className="text-light opacity-30 small d-block fw-bold uppercase">Original</span>
                              <span className="text-danger fw-800 text-decoration-line-through">{formatFileSize(image.originalSize)}</span>
                            </div>
                          </div>
                          <button
                            className="btn btn-primary w-100 py-3 rounded-pill fw-900 d-flex align-items-center justify-content-center gap-2 shadow-lg border-0"
                            style={{ background: 'var(--primary-gradient)' }}
                            onClick={() => handleDownload(image.id, image.filename)}
                          >
                            <FiDownload size={20} /> DOWNLOAD ASSET
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="modal-backdrop fade show" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)' }} onClick={() => setShowModal(false)}></div>}

      <style>{`
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .x-small { font-size: 0.65rem; }
        .uppercase { text-transform: uppercase; }
      `}</style>
    </div>
  );
};